<template>
  <div class="chart-container">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
import { Bar, mixins } from 'vue-chartjs'
const { reactiveProp } = mixins

export default {
  name: 'SagOutputChart',
  extends: Bar,
  mixins: [reactiveProp],
  props: {
    chartData: {
      type: Object,
      required: true,
      validator: (value) => {
        return value.Plan && value.Achieve && value.ProdData
      }
    }
  },
  data() {
    return {
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        scales: {
          xAxes: [{
            stacked: true,
            scaleLabel: {
              display: true,
              labelString: 'Date'
            }
          }],
          yAxes: [{
            id: 'y',
            type: 'linear',
            position: 'left',
            stacked: true,
            scaleLabel: {
              display: true,
              labelString: 'Quantity'
            },
            ticks: {
              callback: (value) => new Intl.NumberFormat().format(value)
            }
          }, {
            id: 'y1',
            type: 'linear',
            position: 'right',
            scaleLabel: {
              display: true,
              labelString: 'Percentage'
            },
            ticks: {
              callback: (value) => `${value}%`
            },
            gridLines: {
              drawOnChartArea: false
            }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex]
              const value = tooltipItem.yLabel
              const label = dataset.label || ''
              
              if (dataset.yAxisID === 'y1') {
                return `${label}: ${value}%`
              }
              return `${label}: ${new Intl.NumberFormat().format(value)}`
            }
          }
        },
        legend: {
          position: 'bottom'
        }
      }
    }
  },
  computed: {
    chartDataComputed() {
      // 獲取所有唯一的日期
      const allDates = [...new Set([
        ...this.chartData.Plan.map(item => item.Date),
        ...this.chartData.Achieve.map(item => item.Date),
        ...this.chartData.ProdData.map(item => item.Date)
      ])].sort()

      // 格式化日期
      const dates = allDates.map(date => {
        const d = new Date(date)
        return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`
      })

      // 處理計劃數據
      const planData = dates.map(date => {
        const planItem = this.chartData.Plan.find(item => {
          const d = new Date(item.Date)
          const formattedDate = `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`
          return formattedDate === date
        })
        return planItem ? parseInt(planItem.Value) : 0
      })

      // 處理達成率數據
      const achieveData = dates.map(date => {
        const achieveItem = this.chartData.Achieve.find(item => {
          const d = new Date(item.Date)
          const formattedDate = `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`
          return formattedDate === date
        })
        return achieveItem ? parseFloat(achieveItem.Value.replace('%', '')) : 0
      })

      // 處理產出數據
      const uniqueLabels = [...new Set(this.chartData.ProdData.map(item => item.Label))]
      const stackedData = {}

      uniqueLabels.forEach(label => {
        stackedData[label] = dates.map(date => {
          const prodItem = this.chartData.ProdData.find(item => {
            const d = new Date(item.Date)
            const formattedDate = `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`
            return formattedDate === date && item.Label === label
          })
          return prodItem ? parseInt(prodItem.Value) : 0
        })
      })

      // 生成顏色
      const colors = [
        'rgba(255, 99, 132, 0.7)',
        'rgba(54, 162, 235, 0.7)',
        'rgba(255, 206, 86, 0.7)',
        'rgba(75, 192, 192, 0.7)',
        'rgba(153, 102, 255, 0.7)'
      ]

      // 創建數據集
      const datasets = [
        // 堆疊柱狀圖數據集
        ...Object.entries(stackedData).map(([label, data], index) => ({
          type: 'bar',
          label,
          data,
          backgroundColor: colors[index % colors.length],
          stack: 'Stack 0'
        })),
        // 計劃線圖數據集
        {
          type: 'line',
          label: 'Plan',
          data: planData,
          borderColor: 'rgb(75, 192, 192)',
          borderWidth: 2,
          tension: 0.1,
          yAxisID: 'y',
          pointRadius: 5,
          pointHoverRadius: 8,
          fill: false
        },
        // 達成率線圖數據集
        {
          type: 'line',
          label: 'Achieve',
          data: achieveData,
          borderColor: 'rgb(255, 159, 64)',
          borderWidth: 2,
          tension: 0.1,
          yAxisID: 'y1',
          pointRadius: 5,
          pointHoverRadius: 8,
          fill: false,
          pointStyle: 'circle',
          pointBackgroundColor: 'white',
          pointBorderColor: 'rgb(255, 159, 64)',
          pointBorderWidth: 2
        }
      ]

      return {
        labels: dates,
        datasets
      }
    }
  },
  mounted() {
    this.renderChart(this.chartDataComputed, this.options)
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        if (this.$data._chart) {
          this.$data._chart.destroy()
        }
        this.renderChart(this.chartDataComputed, this.options)
      }
    }
  }
}
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
  height: 400px;
}
</style>
